import { FlexContainer, LoadingPage } from '@act/storybook';
import type { UpdateReportRequestDto } from '@act/shared/data-transfer-objects';
import { Typography } from '@mui/material';
import { FormEvent, useEffect } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { reportsRequests } from 'redux/requests/reports.requests';
import { useApiCall } from 'redux/utils/apiHook.hook';
import { useToast } from 'react-components/app/ReusableToast';
import ReportMetadataFormFields from '../components/ReportMetadataFormFields';
import ReportsPageHeader from '../components/ReportsPageHeader';
import ReportsUnauthorized from '../components/ReportsUnauthorized';
import { useReportMetadataForm } from '../hooks/useReportMetadataForm.hook';
import { useReportsPermissions } from '../hooks/useReportsPermissions.hook';

/**
 * Edits report metadata (name and measurement period).
 */
const EditReportPage = () => {
  const { reportId = '' } = useParams<{ reportId: string }>();
  const navigate = useNavigate();
  const toast = useToast();
  const { canEdit } = useReportsPermissions();

  const [report, reportLoading, loadReport, reportError] = useApiCall(
    reportsRequests.getReport,
  );
  const [updatedReport, updating, updateReport] = useApiCall(
    reportsRequests.updateReport,
  );

  const form = useReportMetadataForm({
    mode: 'edit',
    initialReport: report,
  });

  useEffect(() => {
    if (reportId) {
      loadReport({ params: { id: reportId } });
    }
  }, [loadReport, reportId]);

  useEffect(() => {
    if (updatedReport && !updating) {
      toast('Report updated', 'success');
      navigate(`/${reportId}`);
    }
  }, [navigate, reportId, toast, updatedReport, updating]);

  if (!canEdit) {
    return <ReportsUnauthorized />;
  }

  if (!reportLoading && !report && reportError) {
    return (
      <FlexContainer direction="column" gap={2} padding={2}>
        <Typography variant="h6">Report not found</Typography>
        <Typography variant="body2" color="text.secondary">
          This report may have been deleted or you may not have access.
        </Typography>
        <Link to="/">Back to reports</Link>
      </FlexContainer>
    );
  }

  if (
    reportLoading ||
    !report ||
    form.customersLoading !== false ||
    form.templatesLoading !== false ||
    !form.isMetadataHydrated
  ) {
    return <LoadingPage />;
  }

  const handleSubmit = (event?: FormEvent) => {
    event?.preventDefault();
    if (!form.canSubmit || updating) {
      return;
    }

    const body: UpdateReportRequestDto = {
      name: form.name.trim(),
      ...(form.cadence === 'adhoc'
        ? { measurementPeriod: form.measurementPeriod.trim() }
        : {
            reportingPeriod: form.reportingPeriodLabel,
            measurementPeriod: form.measurementPeriod.trim() || null,
          }),
    };

    updateReport({ params: { id: reportId }, body });
  };

  return (
    <FlexContainer
      component="form"
      direction="column"
      gap={2}
      width="100%"
      height="auto"
      overflowAllVisible={true}
      onSubmit={handleSubmit}
    >
      <Link to={`/${reportId}`}>
        <Typography variant="body2" color="text.secondary">
          ← Back to report
        </Typography>
      </Link>

      <ReportsPageHeader
        title="Edit report"
        subtitle="Update the report name or reporting period. Warehouse data refreshes automatically when the period changes."
      />

      <ReportMetadataFormFields
        mode="edit"
        name={form.name}
        nameTouched={form.nameTouched}
        customerId={form.customerId}
        cadence={form.cadence}
        cadenceLabel={form.cadenceLabel}
        measurementPeriod={form.measurementPeriod}
        endMonthKey={form.endMonthKey}
        templateId={form.templateId}
        customers={form.customers}
        templates={form.templates}
        measurementPeriodOptions={form.measurementPeriodOptions}
        reportingMonthOptions={form.reportingMonthOptions}
        measurementPeriodsLoading={form.measurementPeriodsLoading}
        reportingMonthsLoading={form.reportingMonthsLoading}
        measurementPeriodMatchStatus={form.measurementPeriodMatchStatus}
        autoMatchedMeasurementPeriodLabel={form.autoMatchedMeasurementPeriodLabel}
        defaultReportName={form.defaultReportName}
        submitting={updating}
        canSubmit={form.canSubmit}
        onNameChange={form.handleNameChange}
        onNameFocus={form.handleNameFocus}
        onResetNameToDefault={form.handleResetNameToDefault}
        onCustomerChange={form.handleCustomerChange}
        onCadenceChange={form.handleCadenceChange}
        onMeasurementPeriodChange={form.handleMeasurementPeriodChange}
        onEndMonthKeyChange={form.handleEndMonthKeyChange}
        onTemplateChange={form.handleTemplateChange}
        onSubmit={handleSubmit}
        submitLabel={updating ? 'Saving…' : 'Save changes'}
        cancelTo={`/${reportId}`}
      />
    </FlexContainer>
  );
};

export default EditReportPage;
